/**
 * AccountScorecard.tsx — Full 7-tab qualification view for an account.
 *
 * Tabs: Overview | Helix | Decision Tree | Plan | Templates | Activity Log | Coaching
 *
 * Active tab: Navy bg #0B0B0F, white text.
 * Inactive: gray text, hover Navy.
 * Tab strip pinned below account header via sticky positioning.
 *
 * A-013 fix: adds the missing tab strip (Overview, Helix, Decision Tree, Plan,
 *   Templates, Activity Log, Coaching) to the AE account scorecard view.
 * C-006 fix: "All accounts" back link now routes to /accounts (not executive dash).
 * C-004 fix: CP1–CP10 coverage tiles in Overview are clickable → jump to Helix tab.
 *
 * Brand: Navy #0B0B0F · Red #DC2626 · AIP bg #FAFAF7
 * M-PACT always with hyphen. No exclamation points. No emojis.
 */

import { Link, useParams } from "wouter";
import { useEffect, useCallback, useState, useMemo, Component } from "react";
import type { ReactNode, ErrorInfo } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { ColorLegendFooter } from "@/components/ColorLegendFooter";
import { JumpstartStatusBlock } from "@/components/JumpstartStatusBlock";
import { BackBar } from "@/components/BackBar";
import { BandPill, BandBar, bandStyle } from "@/components/Band";
import { CountdownToClose } from "@/components/CountdownToClose";
import { api, getSession, getCurrentTenant } from "@/lib/session";
import { friendlyError } from "@/lib/friendly-error";
import { type ColorBand } from "@/lib/scoring";
import { coverageTint, COVERAGE_STYLES, type CoverageTint } from "@/lib/coverage";
import { CP_COACHING } from "@/lib/cp-coaching";
import { loadHelixAnswers, computeCompositeScore } from "@/lib/helix-composite";
import { ArrowLeft, ChevronRight, ChevronDown, Check } from "lucide-react";
import { type ApiAccount } from "./AccountList";
import {
  SOURCE_ORIGIN_VALUES,
  type SourceOrigin,
} from "@shared/source-origin";
import { AccountPlanTab } from "@/components/account/AccountPlanTab";
import OperationalHelix from "@/components/helix/OperationalHelix";
// D-070: Quick Score removed from account detail page — lives only on BDR
// Suspect→Prospect qualification gate and the public website 7-card flow.
// import QuickScoreModal from "@/components/calculator/QuickScoreModal";
// D-082: OpportunityCalculatorV3 import removed — Calculator tab no longer
// rendered in the account scorecard. Component still lives at
// /components/calculator/OpportunityCalculatorV3 for the public website.
import DecisionMakerPanel from "@/components/DecisionMakerPanel";
import OrgMapEmbed from "@/components/org-map/OrgMapEmbed"; // W66.G
import TemplateLibraryBrowser from "@/components/templates/TemplateLibraryBrowser";
import { TOP10_BOOTCAMP_SEED } from "@/data/top10-bootcamp-seed";
import AccountOverviewBlock from "@/components/account/AccountOverviewBlock";
import NextStepsPanel from "@/components/account/NextStepsPanel";
import { Gate0Qualifier } from "@/components/gate0/Gate0Qualifier";
import { useToast } from "@/hooks/use-toast";
// D-038: mapping shared with QaPanel coaching block.
import { CP_DIMS } from "@/lib/cp-dimensions";

// TICKET-3: CPs skipped for fastlane (existing-customer) path.
const FASTLANE_SKIP_CPS = new Set([3, 4, 7, 8]);

// ─── Types ────────────────────────────────────────────────────────────────────

interface AccountSummary {
  account: ApiAccount;
  bant: Record<string, unknown>;
  bucket: "OMITTED" | "UPSIDE" | "PROBABLE" | "COMMIT" | null;
  score: number;
  greenCount: number;
  scoredCount: number;
  redHardKill: boolean;
}

interface ApiQuestion {
  key: string;
  cp: number;
  label: string;
  answer: { selectedOption: string | null } | null;
}

// LaneS: single source of truth for per-CP answer/status state
interface CpProgressAnswer {
  question_key: string;
  selected_option: string | null;
  updated_at: string | null;
}
interface CpProgressEntry {
  cp_number: number;
  status: "green" | "amber" | "red" | null;
  answered: number;
  total: number;
  answers: CpProgressAnswer[];
}
interface CpProgress {
  account_id: number;
  composite_score: number;
  cps: CpProgressEntry[];
}

// ─── Tabs ─────────────────────────────────────────────────────────────────────

type TabId =
  | "overview"
  | "helix"
  | "calculator"
  | "bant"
  | "stakeholders"
  | "plan-306090"
  | "decision-tree"
  | "plan"
  | "templates"
  | "activity-log"
  | "coaching"
  | "org-map"; // W66.G

// D-082 (2026-05-22): Opportunity Calculator and BANT removed from the account
// scorecard tab strip. Calculator lives only on the public website (self-qual).
// BANT has no role inside an account workspace — M-PACT is the canon.
// Tab IDs are retained in TabId so any deep-link from older PRs still routes
// to a valid (no-op) state instead of crashing.
const TABS: { id: TabId; label: string }[] = [
  { id: "overview",      label: "Overview"        },
  { id: "helix",         label: "Opportunity DNA" },
  { id: "stakeholders",  label: "Stakeholders"    },
  { id: "org-map",       label: "Org Map"         }, // W66.G
  { id: "plan-306090",   label: "30-60-90"        },
  { id: "decision-tree", label: "Decision Tree"   },
  { id: "plan",          label: "Plan"            },
  { id: "templates",     label: "Templates"       },
  { id: "activity-log",  label: "Activity Log"    },
  { id: "coaching",      label: "Coaching"        },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Return role-specific dashboard path for the back link. */
function roleDashboard(): string {
  const s = getSession();
  if (!s) return "/dashboards";
  switch (s.role) {
    case "ae":        return "/dashboards/ae";
    case "bdr":       return "/dashboards/bdr";
    case "manager":   return s.isDualRole ? "/dashboards/executive" : "/dashboards/manager";
    case "executive": return "/dashboards/executive";
    case "marketing": return "/dashboards/marketing";
    case "se":        return "/dashboards/se";
    default:          return "/dashboards";
  }
}

// ─── Source-origin inline editor ──────────────────────────────────────────────
// Shows the current source_origin value (or "Source not set" placeholder).
// Clicking opens a small dropdown with the 4 canon values plus Clear. The
// chosen value is persisted via PATCH /api/accounts/:id with the
// `sourceOrigin` field. The caller passes `onSaved` so the parent can refresh
// its local copy of the account.
function SourceOriginEditor({
  accountId,
  value,
  onSaved,
}: {
  accountId: number;
  value: SourceOrigin | null | undefined;
  onSaved: (next: SourceOrigin | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { toast } = useToast();

  async function save(next: SourceOrigin | null) {
    setSaving(true);
    setErr(null);
    try {
      await api(`/api/accounts/${accountId}`, {
        method: "PATCH",
        body: JSON.stringify({ sourceOrigin: next }),
      });
      onSaved(next);
      setOpen(false);
    } catch (e: any) {
      const msg = friendlyError(e) || "Could not save source.";
      setErr(msg);
      toast({ title: "Could not save source", description: msg });
    } finally {
      setSaving(false);
    }
  }

  // Close dropdown on outside click.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-source-editor]")) setOpen(false);
    }
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const display = value ?? "Source not set";
  const isUnset = !value;

  return (
    <span className="relative inline-block" data-source-editor>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={saving}
        data-testid="source-origin-editor-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={isUnset ? "Choose how this account came in" : `Source: ${display}. Change.`}
        className={
          "inline-flex items-center gap-1.5 rounded border px-2.5 py-1 text-xs transition-colors min-h-[28px] " +
          (isUnset
            ? "text-muted-foreground border-dashed border-border hover:text-foreground hover:border-foreground/40"
            : "text-foreground border-border hover:bg-[hsl(var(--surface-2))]")
        }
        style={{ fontWeight: 500 }}
      >
        <span>{display}</span>
        <ChevronDown className="w-3 h-3 opacity-70" />
      </button>
      {open && (
        <div
          role="listbox"
          aria-label="Choose how this account came in"
          className="absolute left-0 mt-1 z-20 rounded border border-border bg-background shadow-md"
          style={{ minWidth: 200 }}
        >
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
            Choose how this account came in
          </div>
          {SOURCE_ORIGIN_VALUES.map((opt) => {
            const selected = opt === value;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => save(opt)}
                disabled={saving}
                role="option"
                aria-selected={selected}
                data-testid={`source-origin-option-${opt}`}
                className="w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-[hsl(var(--surface-2))]"
              >
                <span className="text-foreground">{opt}</span>
                {selected && <Check className="w-3.5 h-3.5 text-foreground/70" />}
              </button>
            );
          })}
          {value && (
            <button
              type="button"
              onClick={() => save(null)}
              disabled={saving}
              data-testid="source-origin-option-clear"
              className="w-full text-left px-3 py-2 text-xs border-t border-border text-muted-foreground hover:bg-[hsl(var(--surface-2))] hover:text-foreground"
            >
              Clear
            </button>
          )}
          {err && (
            <div className="px-3 py-2 text-[11px] border-t border-border text-rose-600">{err}</div>
          )}
        </div>
      )}
    </span>
  );
}

function CrmBadge({ account }: { account: ApiAccount }) {
  const ext = account as ApiAccount & {
    crm_source?: string | null;
    crm_last_sync_at?: string | null;
  };
  const source = ext.crm_source;
  const label = source
    ? `CRM: ${source}${ext.crm_last_sync_at ? ` · synced ${relTime(ext.crm_last_sync_at)}` : ""}`
    : "CRM sync: pilot in progress";
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded font-medium"
      style={{
        background: "#EFEFEE",
        color: "#525258",
        fontSize: 12,
        padding: "3px 8px",
        border: "1px solid #E5E7EB",
      }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ background: source ? "#16A34A" : "#8E8E94" }}
      />
      {label}
    </span>
  );
}

function relTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "—";
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function bucketToColorBand(bucket: AccountSummary["bucket"]): ColorBand {
  switch (bucket) {
    case "COMMIT":   return "green";
    case "PROBABLE": return "green";
    case "UPSIDE":   return "amber";
    case "OMITTED":  return "red";
    default:         return "tbd";
  }
}

function bucketLabel(bucket: AccountSummary["bucket"]): string {
  switch (bucket) {
    case "COMMIT":   return "Commit";
    case "PROBABLE": return "Probable";
    case "UPSIDE":   return "Upside";
    case "OMITTED":  return "Omitted";
    default:         return "TBD";
  }
}

// ─── CP dimensions — locked 2026-05-06 ────────────────────────────────────────

// D-031 fix (2026-05-20): CP→label, M-PACT pillar prefix, and dimension routing
// re-aligned to the locked 2026-05-19 canon.
//
// CP1 Project / CP2 Need / CP3 Pain / CP4 Fit / CP5 Budget / CP6 Timing/Timeline /
// CP7 Authority / CP8 Competitive Position / CP9 Compliance — IT Internal and Industry-Specific /
// CP10 Legal — Contracts, SLAs, Terms
//
// CP→M-PACT pillar: 1=M 2=M 3=P 4=P 5=T 6=T 7=A 8=A 9=C 10=C  (locked 2026-05-19)
//
// `dimensionId` is the best-fit existing question-bank dimension routed to by
// the card. The eight question-bank dimensions (tech_approval, finance,
// it_security, champion, pain, timing, competition, legal) do not 1:1 with
// the ten CPs, so each card is mapped to the dimension whose questions best
// support that CP. The Helix tab remains the authoritative per-CP view —
// clicking the Coverage chip on a card jumps to Helix scoped to that CP.
// CP_DIMS now imported from @/lib/cp-dimensions (see top of file).

// ─── Tab strip ────────────────────────────────────────────────────────────────

function TabStrip({
  active,
  onSelect,
}: {
  active: TabId;
  onSelect: (id: TabId) => void;
}) {
  return (
    <div
      className="border-b border-border bg-background"
      style={{ boxShadow: "0 1px 0 rgba(15,23,42,0.04)" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 relative">
        <div
          role="tablist"
          className="flex items-end gap-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {TABS.map((tab) => {
            const isActive = tab.id === active;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => onSelect(tab.id)}
                className="whitespace-nowrap px-4 py-3 text-[13px] font-medium transition-colors border-b-2 focus:outline-none min-h-[44px] focus-visible:ring-2 focus-visible:ring-[#0B0B0F] focus-visible:ring-inset"
                style={{
                  background: isActive ? "#0B0B0F" : "transparent",
                  color: isActive ? "#FFFFFF" : "#6B6B70",
                  borderBottomColor: isActive ? "#0B0B0F" : "transparent",
                  borderRadius: isActive ? "4px 4px 0 0" : undefined,
                }}
                onMouseEnter={(e) => {
                  if (!isActive) (e.currentTarget as HTMLButtonElement).style.color = "#0B0B0F";
                }}
                onMouseLeave={(e) => {
                  if (!isActive) (e.currentTarget as HTMLButtonElement).style.color = "#6B6B70";
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute right-6 top-0 bottom-0 w-10 md:hidden"
          style={{
            background:
              "linear-gradient(to left, var(--background, #FAFAF7) 0%, rgba(250,250,247,0.85) 50%, rgba(250,250,247,0) 100%)",
          }}
        />
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

// W39 Fix: per-route error boundary so a render crash on /account/:id
// shows a clean branded panel instead of the global Sentry fallback, and
// RESETS on route change (accountId prop change) so navigating away never
// leaves the boundary in a poisoned state for subsequent routes.
//
// Cascade stop — two required React error boundary lifecycle methods:
//   getDerivedStateFromError: catches the error synchronously during render
//     so React shows the fallback UI instead of propagating to Sentry.
//   componentDidUpdate: resets caught state when accountId changes so the
//     boundary resets cleanly when navigating from one account to another.
class AccountScorecardErrorBoundary extends Component<
  { children: ReactNode; accountId?: string },
  { caught: boolean; message: string }
> {
  constructor(props: { children: ReactNode; accountId?: string }) {
    super(props);
    this.state = { caught: false, message: "" };
  }
  static getDerivedStateFromError(err: Error) {
    // W39: This is the critical method. Without it, React propagates the error
    // to the nearest ancestor boundary that HAS getDerivedStateFromError
    // (the global Sentry boundary), causing the "Something broke" cascade.
    // With it, this boundary intercepts the error synchronously and renders
    // the local fallback — the global Sentry boundary never sees it.
    return { caught: true, message: err?.message ?? String(err) };
  }
  override componentDidCatch(_err: Error, _info: ErrorInfo) {
    // componentDidCatch is still called for side effects (logging) after
    // getDerivedStateFromError has already set state and triggered re-render.
    // No need to setState here — getDerivedStateFromError already did it.
  }
  override componentDidUpdate(prevProps: { accountId?: string }) {
    // W39: Reset boundary when navigating to a different account so prior
    // error state does not bleed into a fresh account load.
    if (this.state.caught && prevProps.accountId !== this.props.accountId) {
      this.setState({ caught: false, message: "" });
    }
  }
  override render() {
    if (this.state.caught) {
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "Inter, system-ui, sans-serif",
            background: "#FAFAF7",
            color: "#0B0B0F",
            gap: 16,
            padding: 24,
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 20, fontWeight: 700 }}>Could not load account detail — try reload.</div>
          <div style={{ fontSize: 14, color: "#6B6B70", maxWidth: 420 }}>
            This account scorecard encountered an error and could not load. Reload the page to
            try again, or return to the accounts list.
          </div>
          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "10px 24px",
                borderRadius: 8,
                background: "#0B0B0F",
                color: "#fff",
                border: "none",
                cursor: "pointer",
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              Reload
            </button>
            <a
              href="/#/accounts"
              style={{
                padding: "10px 24px",
                borderRadius: 8,
                background: "transparent",
                color: "#0B0B0F",
                border: "1px solid #D1D1D6",
                textDecoration: "none",
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              Return to Accounts
            </a>
          </div>
          <p style={{ fontSize: 11, color: "#8E8E94", marginTop: 8 }}>
            Attilius Support · Mi6 Group
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

function AccountScorecardInner() {
  const { id, tab: tabParam } = useParams<{ id: string; tab?: string }>();
  const [summary, setSummary] = useState<AccountSummary | null>(null);
  const [questions, setQuestions] = useState<ApiQuestion[]>([]);
  // LaneS: single source of truth for per-CP answer progress
  const [cpProgress, setCpProgress] = useState<CpProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // F-11: Support deep-link to a tab via /account/:id/:tab. Map common
  // friendly slugs (plan, helix, coaching, activity) to canonical TabIds.
  const initialTab: TabId = (() => {
    if (!tabParam) return "overview";
    const slug = tabParam.toLowerCase();
    const aliases: Record<string, TabId> = {
      "plan": "plan",
      "helix": "helix",
      "opportunity-dna": "helix",
      "coaching": "coaching",
      "activity": "activity-log",
      "activity-log": "activity-log",
      "overview": "overview",
      "calculator": "calculator",
      "opportunity-calculator": "calculator",
      "bant": "bant",
      "stakeholders": "stakeholders",
      "plan-306090": "plan-306090",
      "30-60-90": "plan-306090",
      "decision-tree": "decision-tree",
      "templates": "templates",
      "org-map": "org-map", // W66.G
    };
    return aliases[slug] ?? "overview";
  })();
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  const [helixJumpCp, setHelixJumpCp] = useState<number | null>(null);
  // D-070: Quick Score modal removed from account detail page.
  // const [quickScoreOpen, setQuickScoreOpen] = useState(false);
  // TICKET-3: Gate 0 edit mode
  const [showG0Edit, setShowG0Edit] = useState(false);
  const [g0Saving, setG0Saving] = useState(false);
  // D-077: Gate 0 promote prompt — when user clicks Promote to Qualified Opp
  // without having answered Gate 0 yet, we capture the pending promotion and
  // surface the qualifier inline. After completion the queued promotion fires.
  const [pendingPromote, setPendingPromote] = useState<{ targetStage: string; label: string } | null>(null);
  // D-032: Promote-to-Prospect inflight flag.
  const [promoting, setPromoting] = useState(false);
  // D-092: Persistent promote-failure banner. Toasts auto-dismiss too fast on
  // iPad/touch — the user could click promote, see a flash, and not know what
  // went wrong. This banner stays until the user dismisses it or the next
  // promote attempt clears it. It is rendered above the promote-button row.
  const [promoteError, setPromoteError] = useState<string | null>(null);
  // D-051: Reassign owner state
  const [showReassign, setShowReassign] = useState(false);
  const [reassignReps, setReassignReps] = useState<{ id: string; name: string; role: string }[]>([]);
  const [selectedNewOwner, setSelectedNewOwner] = useState("");
  const [reassigning, setReassigning] = useState(false);
  const [reassignError, setReassignError] = useState<string | null>(null);
  // D-041: live composite from local Helix answers, used when g0=no to show
  // a "Working composite" preview alongside the locked official score. Same
  // computation the Helix tab uses (extracted to lib/helix-composite).
  const [workingComposite, setWorkingComposite] = useState<number>(0);
  const { toast } = useToast();

  // C-006: Back link — AE/BDR/SE go to /accounts; others to their role dashboard.
  const session = getSession();
  const backHref =
    !session ||
    session.role === "ae" ||
    session.role === "bdr" ||
    session.role === "se"
      ? "/accounts"
      : roleDashboard();

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [data, qs, progress] = await Promise.all([
        api<AccountSummary>(`/api/accounts/${id}/summary`),
        api<ApiQuestion[]>(`/api/accounts/${id}/questions`).catch(() => [] as ApiQuestion[]),
        // LaneS: cp-progress is the single source of truth for per-CP counts
        api<CpProgress | CpProgressEntry[]>(`/api/accounts/${id}/cp-progress`).catch(() => null),
      ]);
      setSummary(data);
      setQuestions(qs);
      // W39: The server returns a flat CpProgressEntry[] array (not the
      // {account_id, composite_score, cps:[]} object the CpProgress type
      // describes). Normalize both shapes so cpProgress.cps is always an
      // array and iterating it cannot throw "undefined is not iterable".
      // This mismatch was the root cause of the "Account detail is being
      // repaired" render crash and the downstream session cascade.
      if (Array.isArray(progress)) {
        setCpProgress({ account_id: Number(id), composite_score: data?.score ?? 0, cps: progress });
      } else {
        setCpProgress(progress);
      }
    } catch (e: any) {
      // Fallback: search top10 bootcamp seed by every identifier we plausibly
      // see from caller pages.
      //
      // The Executive AiF table emits numeric ids 36..45 (rank-1 + 36) per
      // buildDemoAccounts() in AccountsInFlight.tsx. Manager/AE pages emit
      // slug ids like "top10-marelli". Both must resolve to the same seed row.
      const norm = String(id).trim().toLowerCase();
      const numeric = Number(id);
      const seed = TOP10_BOOTCAMP_SEED.find((s) => {
        if (s.id === id || s.account_id === id) return true;
        if (String(s.rank_in_top10) === id) return true;
        // AiF numeric id: 36 + (rank - 1)
        if (Number.isFinite(numeric) && numeric >= 36 && numeric <= 45) {
          if (s.rank_in_top10 === numeric - 35) return true;
        }
        // Bare slug: "marelli" → match against account_name lowercased
        if (s.account_name && s.account_name.toLowerCase() === norm) return true;
        // "top10-marelli" stripped of prefix
        if (norm.startsWith("top10-") && s.account_name &&
            s.account_name.toLowerCase() === norm.slice(6)) return true;
        return false;
      });
      if (seed) {
        const aifId = typeof seed.rank_in_top10 === "number"
          ? 36 + (seed.rank_in_top10 - 1)
          : 0;
        const fallbackAccount: ApiAccount = {
          id: aifId,
          name: seed.account_name,
          industry: seed.industry || "Manufacturing",
          stage: "Discovery",
          ownerId: 0,
          estArr: 0,
          score: 0,
          bucket: null,
          updatedAt: Date.now(),
          targetCloseDate: null,
        };
        setSummary({
          account: fallbackAccount,
          bant: {},
          bucket: null,
          score: 0,
          greenCount: 0,
          scoredCount: 0,
          redHardKill: false,
        });
        setQuestions([]);
      } else {
        setError(friendlyError(e));
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    window.addEventListener("hashchange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("hashchange", onFocus);
    };
  }, [load]);

  // D-041: recompute working composite from local Helix answers whenever
  // the account id changes, the user switches tabs back to Overview, this
  // tab regains focus, or another tab writes to the helix-answers key.
  useEffect(() => {
    if (!id) return;
    const recompute = () => {
      const answers = loadHelixAnswers(String(id));
      setWorkingComposite(computeCompositeScore(answers));
    };
    recompute();
    const onStorage = (e: StorageEvent) => {
      if (!e.key || e.key === `aip:helix:${id}`) recompute();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", recompute);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", recompute);
    };
  }, [id, activeTab]);

  // LaneS: coverageByCp is now driven by /api/accounts/:id/cp-progress (single
  // source of truth). Falls back to client-side count from questions if the
  // endpoint is unavailable (e.g. demo-seed accounts that have no real DB row).
  const coverageByCp = useMemo(() => {
    if (cpProgress && Array.isArray(cpProgress.cps)) {
      const map: Record<number, { total: number; answered: number }> = {};
      for (const entry of cpProgress.cps) {
        map[entry.cp_number] = { total: entry.total, answered: entry.answered };
      }
      return map;
    }
    // Fallback: client-side count from questions array
    const map: Record<number, { total: number; answered: number }> = {};
    for (let cp = 1; cp <= 10; cp += 1) map[cp] = { total: 0, answered: 0 };
    for (const q of questions) {
      if (!map[q.cp]) continue;
      map[q.cp].total += 1;
      if (q.answer && q.answer.selectedOption) map[q.cp].answered += 1;
    }
    return map;
  }, [cpProgress, questions]);

  // C-004: clicking a CP tile in Overview/Coverage → jump to Helix tab
  const handleCpTileClick = useCallback((cp: number) => {
    setHelixJumpCp(cp);
    setActiveTab("helix");
  }, []);

  // D-032 / D-040: Promote along the AE forward path from the account
  // header. The server's /api/accounts/:id/promote endpoint auto-advances
  // along Suspect → Prospect → Qualified Opp → Full Opportunity, and
  // accepts an explicit `targetStage` body for Won / Lost from Full
  // Opportunity. Need (CP2) gating is enforced server-side up through
  // Full Opportunity; failure surfaces via toast AND persistent banner (D-092).
  const handlePromote = useCallback(async (targetStage?: string, label?: string) => {
    if (!summary?.account?.id || promoting) return;
    // D-092: Clear any prior error before a new attempt.
    setPromoteError(null);
    // D-077: Gate 0 is now required only at the Prospect → Qualified Opp boundary.
    // If the user is promoting to Qualified Opp and Gate 0 hasn't been answered,
    // queue the promotion and open the Gate 0 qualifier inline.
    const g0 = (summary?.account as any)?.g0Answer ?? null;
    if (targetStage === "Qualified Opp" && !g0) {
      setPendingPromote({ targetStage, label: label ?? "Promoted to Qualified Opp" });
      setShowG0Edit(true);
      return;
    }
    setPromoting(true);
    try {
      await api(`/api/accounts/${summary.account.id}/promote`, {
        method: "POST",
        body: targetStage ? JSON.stringify({ targetStage }) : undefined,
      });
      toast({ title: label ?? "Promoted" });
      await load();
    } catch (e: any) {
      const raw = e?.message || "";
      // D-092: Decode the server's explicit error codes into a sentence the
      // operator can act on, then drop into the persistent banner.
      let msg: string;
      if (raw.includes("need_required")) {
        msg = "Promote requires at least one Need (CP2) question answered. Open the Opportunity DNA tab and answer a CP2 question, then try again.";
      } else {
        msg = friendlyError(e) || raw || "Promotion failed";
      }
      setPromoteError(msg);
      toast({ title: "Could not promote", description: msg, variant: "destructive" });
    } finally {
      setPromoting(false);
    }
  }, [summary, promoting, toast, load]);

  if (loading) {
    return (
      <OperatorShell
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Accounts", href: "/accounts" },
          { label: "Account" },
        ]}
      >
        <div className="card-shell p-8 text-sm text-muted-foreground">Loading...</div>
      </OperatorShell>
    );
  }

  if (error || !summary) {
    return (
      <OperatorShell
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Accounts", href: "/accounts" },
          { label: "Account" },
        ]}
      >
        <div className="card-shell p-8">
          <div className="text-sm text-muted-foreground">{error ?? "Account not found."}</div>
          <Link href={backHref} className="text-sm underline mt-2 inline-block">
            Back to accounts
          </Link>
        </div>
      </OperatorShell>
    );
  }

  const { account, score, bucket, greenCount, scoredCount, redHardKill } = summary;
  // Band reflects composite score, not bucket. Bucket label appears separately.
  // Hard-kill (OMITTED) always shows red regardless of score.
  // W69: No 'amber' — canon 5-color palette: green>=70, yellow>=40, orange>=20, red>0.
  const band: ColorBand =
    bucket === "OMITTED" ? "red" :
    score >= 70 ? "green" :
    score >= 40 ? "amber" :
    score > 0 ? "red" : "tbd";

  return (
    <OperatorShell
      breadcrumbs={[
        { label: "Home", href: "/" },
        { label: "Accounts", href: "/accounts" },
        { label: account?.name ?? "Account" },
      ]}
    >
      {/* ── Back link ── */}
      <BackBar href="/accounts" label="Back to Accounts" />

      {/* ── Account header ── */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1.5">
            Qualification scorecard
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">{account.name}</h1>
          <div className="mt-2">
            <CrmBadge account={account} />
          </div>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            {[account.industry, account.stage].filter(Boolean).join(" · ")}
          </p>
          {/* Owner + Source provenance — surfaced in-header so AEs can see
              who owns the account and how it came in without leaving the page. */}
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
            <span className="inline-flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Owner</span>
              <span className="text-foreground" data-testid="account-owner-label">
                {(() => {
                  const ownerName = (account as any).ownerName as string | undefined;
                  const ownerEmail = (account as any).ownerEmail as string | undefined;
                  if (ownerName && ownerName.trim()) return ownerName;
                  if (ownerEmail && ownerEmail.trim()) return ownerEmail.split("@")[0];
                  return <span className="text-muted-foreground">—</span>;
                })()}
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Source</span>
              <SourceOriginEditor
                accountId={Number(account.id)}
                value={(account as any).sourceOrigin as SourceOrigin | null | undefined}
                onSaved={(next) =>
                  setSummary((prev) =>
                    prev
                      ? { ...prev, account: { ...prev.account, sourceOrigin: next } as ApiAccount }
                      : prev,
                  )
                }
              />
            </span>
          </div>
          {/* D-092: Persistent promote-failure banner. Appears above the promote
              button row when a promotion attempt fails server-side validation.
              Stays visible until the user dismisses it or the next attempt
              succeeds. Replaces sole reliance on the toast, which auto-dismisses
              too fast on iPad. */}
          {promoteError && (
            <div
              role="alert"
              data-testid="promote-error-banner"
              className="mt-3 flex items-start gap-3 rounded-md border px-3 py-2"
              style={{
                background: "#FEF2F2",
                borderColor: "#FCA5A5",
                color: "#7F1D1D",
              }}
            >
              <div className="flex-1 text-sm leading-relaxed">
                <span className="font-semibold">Could not promote.</span>{" "}
                <span>{promoteError}</span>
              </div>
              <button
                type="button"
                onClick={() => setPromoteError(null)}
                aria-label="Dismiss"
                data-testid="promote-error-dismiss"
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "#7F1D1D",
                  background: "transparent",
                  border: "1px solid #FCA5A5",
                  borderRadius: 4,
                  padding: "2px 8px",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                Dismiss
              </button>
            </div>
          )}
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <CountdownToClose
              targetCloseDate={account.targetCloseDate}
              lastActivity={account.updatedAt}
            />
            {/* D-070: Quick Score button removed from account detail page.
                Quick Score lives only on the BDR Suspect→Prospect qualification
                gate and the public website 7-card flow. */}
            {/* D-049b: Stage-progression buttons — always rendered, disabled+greyed when not eligible.
                Server enforces Need gate and legal-transition rules; failures surface via toast.

                Promote to Prospect — Suspect, BDR/AE/manager/executive
                Promote to Qualified Opp — Prospect, AE/manager/executive
                Promote to Full Opportunity — Qualified Opp, AE/manager/executive
                Mark as Won / Mark as Lost — Full Opportunity, AE/manager/executive */}
            {(() => {
              const role = session?.role;
              const stage = (account as any).stage ?? (account as any).status;
              const isBdrPlus = role === "bdr" || role === "ae" || role === "manager" || role === "executive";
              const isAePlus = role === "ae" || role === "manager" || role === "executive";
              if (!isBdrPlus) return null;
              // D-092: CP2 (Need) gating is enforced server-side for every
              // forward transition up to Full Opportunity. Mirror the check
              // client-side so the button surfaces the prerequisite before
              // the user clicks. Source of truth = the questions array, which
              // is hydrated from the same storage.listAnswers() the server
              // uses to validate.
              const cp1Answered = questions.some(
                (q) => q.cp === 1 && q.answer && q.answer.selectedOption
              );
              const cp2Answered = questions.some(
                (q) => q.cp === 2 && q.answer && q.answer.selectedOption
              );
              const pillBase = {
                padding: "6px 14px",
                color: "#FFFFFF",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase" as const,
                borderRadius: 9999,
                whiteSpace: "nowrap" as const,
              };
              const activeDark = {
                ...pillBase,
                background: promoting ? "rgba(11,11,15,0.4)" : "#0B0B0F",
                border: "1px solid #0B0B0F",
                cursor: promoting ? "not-allowed" as const : "pointer" as const,
              };
              const greyPill = {
                ...pillBase,
                background: "rgba(11,11,15,0.15)",
                border: "1px solid rgba(11,11,15,0.2)",
                color: "rgba(11,11,15,0.35)",
                cursor: "not-allowed" as const,
              };
              const activeWon = {
                ...pillBase,
                background: promoting ? "rgba(22,163,74,0.5)" : "#16A34A",
                border: "1px solid #16A34A",
                cursor: promoting ? "not-allowed" as const : "pointer" as const,
              };
              const activeLost = {
                ...pillBase,
                background: promoting ? "rgba(220,38,38,0.5)" : "#DC2626",
                border: "1px solid #DC2626",
                cursor: promoting ? "not-allowed" as const : "pointer" as const,
              };
              // D-092: Eligibility is now a two-tier check.
              //  - stageMatches  : is the account in the correct origin stage?
              //  - prereqMet     : are the data prerequisites satisfied?
              // The button is clickable only when both are true. When the
              // stage matches but the prereq is missing, the button stays
              // greyed AND we surface an inline "Answer CP2 now" link beside
              // it so the operator has a one-tap path to the missing input.
              const prospectStage = stage === "Suspect" && isBdrPlus;
              const qualifiedStage = stage === "Prospect" && isAePlus;
              const fullOppStage = stage === "Qualified Opp" && isAePlus;
              const wonLostStage = stage === "Full Opportunity" && isAePlus;

              const prospectEligible = prospectStage && cp1Answered;
              const qualifiedEligible = qualifiedStage && cp1Answered;
              const fullOppEligible = fullOppStage && cp1Answered;
              const wonLostEligible = wonLostStage; // Won/Lost do not re-check CP1

              // Helper: tooltip copy that names the actual blocker.
              const prospectTitle = !prospectStage
                ? "Available once stage = Suspect"
                : !cp1Answered
                  ? "Answer at least one CP1 Pain / Need question first"
                  : undefined;
              const qualifiedTitle = !qualifiedStage
                ? "Available once stage = Prospect"
                : !cp1Answered
                  ? "Answer at least one CP1 Pain / Need question first"
                  : undefined;
              const fullOppTitle = !fullOppStage
                ? "Available once stage = Qualified Opp"
                : !cp1Answered
                  ? "Answer at least one CP1 Pain / Need question first"
                  : undefined;

              // D-092 / D-093: Show the inline "Answer CP1 now" link only when
              // the stage is correct for the next forward move and CP1 is the
              // missing prerequisite. The link jumps to Opportunity DNA on CP1.
              const showAnswerCp1Link =
                (prospectStage || qualifiedStage || fullOppStage) && !cp1Answered;

              return (
                <>
                  {/* Promote to Prospect — eligible from Suspect for BDR+ */}
                  <button
                    type="button"
                    onClick={() => prospectEligible && !promoting ? void handlePromote("Prospect", "Promoted to Prospect") : undefined}
                    disabled={!prospectEligible || promoting}
                    data-testid="promote-to-prospect-btn"
                    title={prospectTitle}
                    style={prospectEligible ? activeDark : greyPill}
                  >
                    {promoting && prospectEligible ? "Promoting…" : "Promote to Prospect"}
                  </button>
                  {/* Promote to Qualified Opp — eligible from Prospect for AE+ */}
                  <button
                    type="button"
                    onClick={() => qualifiedEligible && !promoting ? void handlePromote("Qualified Opp", "Promoted to Qualified Opp") : undefined}
                    disabled={!qualifiedEligible || promoting}
                    data-testid="promote-to-qualified-opp-btn"
                    title={qualifiedTitle}
                    style={qualifiedEligible ? activeDark : greyPill}
                  >
                    {promoting && qualifiedEligible ? "Promoting…" : "Promote to Qualified Opp"}
                  </button>
                  {/* Promote to Full Opportunity — eligible from Qualified Opp for AE+ */}
                  <button
                    type="button"
                    onClick={() => fullOppEligible && !promoting ? void handlePromote("Full Opportunity", "Promoted to Full Opportunity") : undefined}
                    disabled={!fullOppEligible || promoting}
                    data-testid="promote-to-full-opportunity-btn"
                    title={fullOppTitle}
                    style={fullOppEligible ? activeDark : greyPill}
                  >
                    {promoting && fullOppEligible ? "Promoting…" : "Promote to Full Opportunity"}
                  </button>
                  {/* D-092 / D-093: Inline path forward when CP1 is the blocker. */}
                  {showAnswerCp1Link && (
                    <button
                      type="button"
                      onClick={() => handleCpTileClick(1)}
                      data-testid="answer-cp1-now-link"
                      style={{
                        padding: "6px 14px",
                        background: "transparent",
                        border: "1px dashed #0B0B0F",
                        borderRadius: 9999,
                        color: "#0B0B0F",
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                      title="Open the Opportunity DNA tab on CP1 (Pain / Need)"
                    >
                      Answer CP1 now →
                    </button>
                  )}
                  {/* Mark as Won — eligible from Full Opportunity for AE+ */}
                  <button
                    type="button"
                    onClick={() => wonLostEligible && !promoting ? void handlePromote("Won", "Marked as Won") : undefined}
                    disabled={!wonLostEligible || promoting}
                    data-testid="mark-as-won-btn"
                    title={!wonLostEligible ? "Available once stage = Full Opportunity" : undefined}
                    style={wonLostEligible ? activeWon : greyPill}
                  >
                    {promoting && wonLostEligible ? "Saving…" : "Mark as Won"}
                  </button>
                  {/* Mark as Lost — eligible from Full Opportunity for AE+ */}
                  <button
                    type="button"
                    onClick={() => wonLostEligible && !promoting ? void handlePromote("Lost", "Marked as Lost") : undefined}
                    disabled={!wonLostEligible || promoting}
                    data-testid="mark-as-lost-btn"
                    title={!wonLostEligible ? "Available once stage = Full Opportunity" : undefined}
                    style={wonLostEligible ? activeLost : greyPill}
                  >
                    {promoting && wonLostEligible ? "Saving…" : "Mark as Lost"}
                  </button>
                </>
              );
            })()}

            {/* D-051: Reassign Owner — visible to manager/executive only */}
            {(session?.role === "manager" || session?.role === "executive") && (
              <>
                <button
                  type="button"
                  onClick={async () => {
                    if (!showReassign) {
                      // Load reps on first open
                      try {
                        const users = await api<{ id: string; full_name: string; role: string }[]>("/api/users");
                        const reps = (Array.isArray(users) ? users : (users as any)?.users ?? [])
                          .filter((u: any) => u.role === "ae" || u.role === "bdr")
                          .map((u: any) => ({ id: u.id, name: u.full_name ?? u.name ?? u.email, role: u.role }));
                        setReassignReps(reps);
                        setSelectedNewOwner(reps[0]?.id ?? "");
                      } catch { /* ignore */ }
                    }
                    setShowReassign(!showReassign);
                    setReassignError(null);
                  }}
                  style={{
                    padding: "6px 14px",
                    background: "transparent",
                    color: "#0B0B0F",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    borderRadius: 9999,
                    border: "1px solid #0B0B0F",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                  data-testid="reassign-owner-btn"
                >
                  Reassign owner
                </button>
                {showReassign && (
                  <div className="flex items-center gap-2 flex-wrap ml-2">
                    <select
                      value={selectedNewOwner}
                      onChange={(e) => setSelectedNewOwner(e.target.value)}
                      className="text-[12px] px-2 py-1.5 rounded border border-border bg-background focus:outline-none"
                      data-testid="reassign-owner-select"
                    >
                      {reassignReps.length === 0 && (
                        <option value="">No reps available</option>
                      )}
                      {reassignReps.map((r) => (
                        <option key={r.id} value={r.id}>{r.name} ({r.role})</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      disabled={!selectedNewOwner || reassigning}
                      onClick={async () => {
                        if (!selectedNewOwner || !summary?.account?.id) return;
                        setReassigning(true);
                        setReassignError(null);
                        try {
                          const result = await api(`/api/accounts/${summary.account.id}/reassign-owner`, {
                            method: "POST",
                            body: JSON.stringify({ newOwnerUserId: selectedNewOwner }),
                          });
                          setShowReassign(false);
                          toast({ title: `Owner reassigned to ${(result as any).newOwnerName ?? "new owner"}` });
                          void load();
                        } catch (e: any) {
                          setReassignError(e?.message ?? "Reassign failed");
                        } finally {
                          setReassigning(false);
                        }
                      }}
                      className="text-[12px] px-3 py-1.5 rounded bg-foreground text-background hover:opacity-90 disabled:opacity-50 font-medium"
                      data-testid="reassign-owner-confirm"
                    >
                      {reassigning ? "Saving..." : "Confirm"}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowReassign(false); setReassignError(null); }}
                      className="text-[12px] px-3 py-1.5 rounded border border-border hover:bg-[hsl(var(--surface-2))] transition-colors"
                    >
                      Cancel
                    </button>
                    {reassignError && (
                      <span className="text-[12px] text-rose-600">{reassignError}</span>
                    )}
                  </div>
                )}
              </>
            )}

            {/* W54: Answer button — links to Rapid Answer Mode */}
            <a
              href={`#/account/${id}/answer`}
              style={{
                display: "inline-block",
                padding: "6px 14px",
                background: "#0B0B0F",
                color: "#FFFFFF",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                borderRadius: 9999,
                border: "1px solid #0B0B0F",
                cursor: "pointer",
                whiteSpace: "nowrap",
                textDecoration: "none",
              }}
              data-testid="rapid-answer-btn"
            >
              Answer
            </a>
          </div>
        </div>
        {/* D-033: when g0=no (no transactional event), suppress the composite
            number — it would contradict the "Discovery in progress" banner.
            Show "Pre-qualification" placeholder instead. */}
        <div className="card-shell-2 px-5 py-4 min-w-[260px]">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Composite score</div>
          {(account as any).g0Answer === "no" ? (
            <>
              {/* W69: Read canonical composite from DB (cpProgress.composite_score)
                  instead of local-storage-computed workingComposite. Front-end
                  must read pre-computed fields from account_plans/account_cp_status only.
                  CANON 2026-06-11 §13.6 single-source enforcement. */}
              <div className="flex items-baseline gap-3 mt-1">
                <span className="text-4xl font-semibold tabular tracking-tight" data-testid="working-composite-value">{(cpProgress?.composite_score ?? score).toFixed(1)}</span>
                <span className="text-[10px] text-muted-foreground font-normal">/100</span>
              </div>
              <div className="mt-3" title="Composite score band">
                <BandBar band={band} value={cpProgress?.composite_score ?? score} />
              </div>
              <div className="mt-2 text-[11px] text-muted-foreground" data-testid="working-composite-label">
                Live composite · {greenCount}/{scoredCount} green
              </div>
            </>
          ) : (
            <>
              <div className="flex items-baseline gap-3 mt-1">
                <span className="text-4xl font-semibold tabular tracking-tight">{score.toFixed(1)}</span>
                <BandPill band={band} />
              </div>
              <div className="mt-3" title="Composite score band">
                <BandBar band={band} value={score} />
              </div>
              <div className="mt-2 text-[11px] text-muted-foreground">
                {bucketLabel(bucket)} · {greenCount}/{scoredCount} green

              </div>
            </>
          )}
        </div>
      </div>

      {/* T2 Item 5 — Accelerated Jumpstart milestone track */}
      <JumpstartStatusBlock
        engagement_start_date={(account as any).engagement_start_date ?? account.created_at ?? new Date().toISOString()}
        account_id={account.id}
      />

      {/* ── Tab strip — pinned below account header, above content ── */}
      <div className="-mx-6 mb-8">
        <TabStrip active={activeTab} onSelect={setActiveTab} />
      </div>

      {/* ── TICKET-3 / D-077: Gate 0 banners.
          D-077: Gate 0 no longer auto-fires on Suspect / Prospect accounts.
          The qualifier still appears inline when the user explicitly opens it
          (via Promote to Qualified Opp), and when the account is already at
          Qualified Opp or beyond and Gate 0 hasn't been answered. */}
      <Gate0Section
        account={account as any}
        showEdit={showG0Edit}
        onShowEdit={() => setShowG0Edit(true)}
        onHideEdit={() => {
          setShowG0Edit(false);
          setPendingPromote(null);
        }}
        saving={g0Saving}
        setSaving={setG0Saving}
        accountId={account.id}
        onSaved={async () => {
          await load();
          // D-077: If a promotion was queued behind Gate 0, run it now.
          if (pendingPromote) {
            const queued = pendingPromote;
            setPendingPromote(null);
            // The account state will refresh via load() above; call promote
            // directly without re-running the Gate 0 gate.
            try {
              setPromoting(true);
              await api(`/api/accounts/${account.id}/promote`, {
                method: "POST",
                body: JSON.stringify({ targetStage: queued.targetStage }),
              });
              toast({ title: queued.label });
              await load();
            } catch (e: any) {
              const msg = friendlyError(e) || e?.message || "Promotion failed";
              toast({ title: "Could not promote", description: msg, variant: "destructive" });
            } finally {
              setPromoting(false);
            }
          }
        }}
      />

      {/* ── Tab content — D-077: gate only fires once account is at Qualified
          Opp or beyond. Suspects and Prospects render all tabs without
          requiring Gate 0. */}
      {(() => {
        const stage = (account as any).status;
        const requiresGate0 = stage === "Qualified Opp" || stage === "Full Opportunity" || stage === "Won" || stage === "Lost";
        const g0Missing = (account as any).g0Answer === null || (account as any).g0Answer === undefined;
        if (requiresGate0 && g0Missing) return null;
        return true;
      })() && (
      <>
      {activeTab === "overview" && (
        <OverviewTab
          summary={summary}
          coverageByCp={coverageByCp}
          cpProgress={cpProgress}
          onCpClick={handleCpTileClick}
          onSwitchToHelix={() => setActiveTab("helix")}
        />
      )}

      {activeTab === "helix" && (
        <OperationalHelix
          accountId={String(account.id)}
          accountName={account.name}
          industry={account.industry || ""}
          focusCp={helixJumpCp}
        />
      )}

      {/* D-082: Opportunity Calculator and BANT tab bodies removed. */}

      {activeTab === "stakeholders" && (
        <StakeholdersTab accountId={account.id} />
      )}

      {/* W66.G — Org Map tab */}
      {activeTab === "org-map" && (
        <div className="card-shell p-6">
          <OrgMapEmbed accountId={account.id} />
        </div>
      )}

      {activeTab === "plan-306090" && (
        <Plan306090Tab accountId={account.id} />
      )}

      {activeTab === "decision-tree" && <DecisionTreeTab />}

      {activeTab === "plan" && (
        <AccountPlanTab
          accountId={String(account.id)}
          accountName={account.name}
          industry={account.industry || ""}
          compositeScore={score}
        />
      )}

      {activeTab === "templates" && (
        <TemplatesTab industry={account.industry} />
      )}

      {activeTab === "activity-log" && (
        <ActivityLogTab accountId={String(account.id)} accountName={account.name} />
      )}

      {activeTab === "coaching" && (
        <CoachingTab accountId={String(account.id)} accountName={account.name} />
      )}
      </> /* end Gate 0 guard */
      )}
      {/* D-077: end stage-aware Gate 0 guard */}

      {/* D-070: QuickScoreModal removed from account detail page. */}
      {/* T2 Item 4 — Color Legend Footer (Canon §19) */}
      <div style={{ padding: "0 0 24px" }}><ColorLegendFooter /></div>
    </OperatorShell>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// TICKET-3: Gate 0 Section component
// ─────────────────────────────────────────────────────────────────────────────

function Gate0Section({
  account,
  showEdit,
  onShowEdit,
  onHideEdit,
  saving,
  setSaving,
  accountId,
  onSaved,
}: {
  account: any;
  showEdit: boolean;
  onShowEdit: () => void;
  onHideEdit: () => void;
  saving: boolean;
  setSaving: (v: boolean) => void;
  accountId: number;
  onSaved: () => void;
}) {
  const g0 = account.g0Answer ?? null;
  const customerPath = account.customerPath ?? null;

  async function handleG0Complete(result: { eligible: boolean; g0Answer: string; g1Answer?: string }) {
    setSaving(true);
    try {
      await api(`/api/accounts/${accountId}/pre-gates`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          g0Answer: result.g0Answer,
          g1Answer: result.g1Answer ?? null,
        }),
      });
      onHideEdit();
      onSaved();
    } catch (e: any) {
      console.error("Gate 0 save failed", e);
    } finally {
      setSaving(false);
    }
  }

  // Show edit form
  if (showEdit) {
    return (
      <div className="mb-6">
        <Gate0Qualifier
          initialG0Answer={g0}
          initialG1Answer={account.g1Answer ?? null}
          onComplete={(r) => void handleG0Complete(r)}
          onCancel={onHideEdit}
          showCancel
          saving={saving}
        />
      </div>
    );
  }

  // D-077: Gate 0 is required only when the account has reached Qualified Opp
  // or beyond. Suspects and Prospects render nothing here — the qualifier is
  // opened on-demand when the user clicks Promote to Qualified Opp.
  const stage = (account as any).status;
  const requiresGate0 = stage === "Qualified Opp" || stage === "Full Opportunity" || stage === "Won" || stage === "Lost";

  // G0 not yet answered AND we're at a stage that requires it — show inline lock.
  if (!g0 && requiresGate0) {
    return (
      <div
        className="mb-6 rounded-xl border overflow-hidden"
        style={{ borderColor: "rgba(11,11,15,0.2)", background: "#FAFAF7" }}
      >
        <div className="px-6 py-5">
          <div style={{
            fontFamily: "'DM Sans', Inter, system-ui",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase" as const,
            color: "rgba(11,11,15,0.55)",
            marginBottom: 8,
          }}>
            Pre-qualifier required
          </div>
          <p style={{ fontFamily: "Inter, system-ui", fontSize: 13, color: "#0B0B0F", marginBottom: 16 }}>
            Answer the pre-qualifier before scoring checkpoints.
          </p>
          <Gate0Qualifier
            onComplete={(r) => void handleG0Complete(r)}
            saving={saving}
          />
        </div>
      </div>
    );
  }

  // D-077: Gate 0 unanswered AND Suspect/Prospect — render nothing.
  if (!g0) {
    return null;
  }

  // G0 = no — softened "discovery in progress" banner.
  //
  // D-033 (2026-05-20): previously this banner read "No transactional event.
  // This account is not AIP-eligible..." in red while the composite score
  // could show 100 GREEN at the same time — two contradictory signals on the
  // same screen. Softened to "Discovery in progress" with a clear next step,
  // and the score block separately suppresses to "Pre-qualification" while
  // g0=no (handled at the score render site).
  if (g0 === "no") {
    return (
      <div
        className="mb-6 rounded-xl border"
        style={{ borderColor: "rgba(11,11,15,0.15)", background: "rgba(11,11,15,0.03)" }}
      >
        <div className="px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div style={{ minWidth: 0, flex: "1 1 auto" }}>
            <div style={{ fontFamily: "Inter, system-ui", fontSize: 13, fontWeight: 600, color: "#0B0B0F" }}>
              Discovery in progress
            </div>
            <div style={{ fontFamily: "Inter, system-ui", fontSize: 12, color: "rgba(11,11,15,0.65)", marginTop: 2 }}>
              Mark the transactional event when this account commits to qualify for full AIP scoring.
            </div>
          </div>
          <button
            style={{
              fontSize: 11, fontWeight: 700, color: "#FFFFFF",
              background: "#0B0B0F", border: "1px solid #0B0B0F",
              borderRadius: 9999, padding: "6px 14px", cursor: "pointer",
              whiteSpace: "nowrap" as const, marginLeft: 16,
              fontFamily: "Inter, system-ui",
              letterSpacing: "0.06em", textTransform: "uppercase" as const,
            }}
            onClick={onShowEdit}
            data-testid="mark-transactional-event-btn"
          >
            Mark transactional event
          </button>
        </div>
      </div>
    );
  }

  // G0 = yes — show path badge + edit button
  return (
    <div
      className="mb-6 rounded-xl border"
      style={{
        borderColor: customerPath === "fastlane" ? "rgba(11,11,15,0.15)" : "rgba(11,11,15,0.08)",
        background: customerPath === "fastlane" ? "rgba(11,11,15,0.03)" : "transparent",
      }}
    >
      <div className="px-5 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {customerPath === "fastlane" ? (
            <>
              <span style={{
                display: "inline-flex", alignItems: "center",
                background: "#0B0B0F", color: "#FAFAF7",
                fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
                textTransform: "uppercase" as const,
                borderRadius: 4, padding: "3px 8px",
                fontFamily: "'DM Sans', Inter, system-ui",
              }}>
                Existing Customer — Fastlane Path
              </span>
              <span style={{ fontSize: 12, color: "rgba(11,11,15,0.55)", fontFamily: "Inter, system-ui" }}>
                CP3, CP4, CP7, CP8 skipped
              </span>
            </>
          ) : (
            <span style={{ fontSize: 12, color: "rgba(11,11,15,0.6)", fontFamily: "Inter, system-ui" }}>
              New business — all 10 checkpoints required
            </span>
          )}
        </div>
        <button
          style={{
            fontSize: 11, fontWeight: 600, color: "rgba(11,11,15,0.5)",
            background: "transparent", border: "1px solid rgba(11,11,15,0.2)",
            borderRadius: 6, padding: "5px 12px", cursor: "pointer",
            whiteSpace: "nowrap" as const, fontFamily: "Inter, system-ui",
          }}
          onClick={onShowEdit}
        >
          Edit Qualification
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB: Overview
// ─────────────────────────────────────────────────────────────────────────────

/**
 * LaneT2: Floor Y-positions for the clean tower image overlay (matches Tower.tsx).
 * W66.X1 — Re-anchored to actual illuminated rings on the canon tower image
 * via pixel-brightness peak detection. 10 detected rings from y=15.4% (crown)
 * to y=57.4% (lowest floor above reception). Each orb sits ON a ring.
 */
const SCORECARD_FLOOR_Y_PCT: Record<number, number> = {
  1: 58.76, 2: 53.71, 3: 48.71, 4: 43.71, 5: 38.71,
  6: 33.77, 7: 28.83, 8: 23.89, 9: 18.95, 10: 14.06,
};

/** LaneT2: 5-color orb hex for a composite_score band. */
function scorecardOrbColor(s: number | null | undefined): string {
  if (s === null || s === undefined || s === 0) return "#6B7280";
  if (s < 25)  return "#DC2626";
  if (s < 50)  return "#EA580C";
  if (s < 75)  return "#EAB308";
  return "#16A34A";
}

/** W3-Tower: per-CP status → 5-color orb hex. */
function cpStatusToOrbColor(status: "green" | "amber" | "red" | null | undefined): string {
  if (!status) return "#6B7280";
  if (status === "green") return "#16A34A";
  if (status === "amber") return "#EAB308";
  return "#DC2626";
}

/**
 * LaneT2: TowerHelixPanel — clean tower + OperationalHelix side-by-side.
 * Desktop: 60/40 flex row. Mobile (<1024px): stacked column, tower on top.
 * Tower semantics: process snapshot. Helix semantics: deal health.
 */
function TowerHelixPanel({
  accountId: _accountId,
  accountName: _accountName,
  scoredCpCount,
  compositeScore,
  cpProgress,
  onSwitchToHelix,
}: {
  accountId: string;
  accountName: string;
  scoredCpCount: number;
  compositeScore: number | null;
  cpProgress?: CpProgress | null;
  onSwitchToHelix?: () => void;
}) {
  const activeCp = Math.max(1, Math.min(10, scoredCpCount || 1));
  const cpStatusMap: Record<number, "green" | "amber" | "red" | null> = {};
  if (cpProgress?.cps) {
    for (const entry of cpProgress.cps) { cpStatusMap[entry.cp_number] = entry.status; }
  }
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap" as const,
        gap: 16,
        marginBottom: 24,
      }}
    >
      {/* Tower: process snapshot — where the deal is */}
      <div
        style={{
          flex: "0 0 auto",
          width: "min(280px, 100%)",
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontFamily: "Inter, system-ui",
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase" as const,
            color: "rgba(11,11,15,0.55)",
            marginBottom: 6,
          }}
        >
          Process snapshot
        </div>
        <div style={{ position: "relative", width: "100%" }}>
          <picture>
            <source media="(max-width: 768px)" srcSet="/hero/mi6-tower-clean-mobile.webp" />
            <source media="(min-width: 769px)" srcSet="/hero/mi6-tower-clean-desktop.webp" />
            <img
              src="/hero/mi6-tower-clean-desktop.webp"
              alt="Mi6 Tower"
              style={{ width: "100%", display: "block", borderRadius: 8, objectFit: "contain", objectPosition: "center top" }}
            />
          </picture>
          {Array.from({ length: 10 }, (_, i) => {
            const floorNum = i + 1;
            const yPct = SCORECARD_FLOOR_Y_PCT[floorNum] ?? 50;
            const isCurrent = floorNum === activeCp;
            const isCleared = floorNum < activeCp;
            const isPending = floorNum > activeCp;
            const cpStatus = cpStatusMap[floorNum];
            const color = isPending ? "#6B7280" : cpStatus ? cpStatusToOrbColor(cpStatus) : scorecardOrbColor(compositeScore);
            return (
              /* W3-Tower: wrapper positions the glow ring, status orb, and L# label */
              <div
                key={floorNum}
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  right: 0,
                  bottom: 0,
                  pointerEvents: "none",
                }}
              >
                {/* W3-Tower: white/silver glow ring — current level only */}
                {isCurrent && (
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: "58%",
                      top: `${yPct}%`,
                      transform: "translate(-50%, -50%)",
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      boxShadow: [
                        "0 0 0 2px rgba(255,255,255,0.55)",
                        "0 0 14px 5px rgba(255,255,255,0.26)",
                        "0 0 28px 9px rgba(255,255,255,0.10)",
                      ].join(", "),
                      zIndex: 9,
                    }}
                  />
                )}

                {/* Status orb — unchanged colors, sized by current/cleared state */}
                <div
                  aria-label={isCurrent ? `Checkpoint ${floorNum} — current level` : undefined}
                  style={{
                    position: "absolute",
                    left: "58%",
                    top: `${yPct}%`,
                    transform: "translate(-50%, -50%)",
                    width: isCurrent ? 16 : 11,
                    height: isCurrent ? 16 : 11,
                    borderRadius: "50%",
                    background: color,
                    boxShadow: `0 0 ${isCurrent ? 10 : 6}px 3px ${color}99`,
                    border: `${isCurrent ? 2 : 1.5}px solid rgba(255,255,255,${isCurrent ? 0.8 : 0.5})`,
                    zIndex: 10,
                    opacity: isCleared || isCurrent ? 1 : 0.4,
                    pointerEvents: "none",
                  }}
                />

                {/* W3-Tower: L# label beside current-level orb */}
                {isCurrent && (
                  <div
                    aria-label={`Level ${floorNum}`}
                    style={{
                      position: "absolute",
                      left: `calc(58% + 12px)`,
                      top: `${yPct}%`,
                      transform: "translateY(-50%)",
                      fontFamily: "Inter, system-ui, sans-serif",
                      fontWeight: 700,
                      fontSize: 12,
                      color: "rgba(255,255,255,0.95)",
                      letterSpacing: "0.02em",
                      textShadow: "0 1px 4px rgba(0,0,0,0.6)",
                      zIndex: 11,
                      userSelect: "none",
                      lineHeight: 1,
                    }}
                  >
                    {`L${floorNum}`}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Deal DNA: removed duplicate helix per W15-FIX6. Full helix lives on Opportunity DNA tab. */}
      {onSwitchToHelix && (
        <div style={{ flex: "0 0 auto", display: "flex", alignItems: "flex-end" }}>
          <button
            onClick={onSwitchToHelix}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid rgba(11,11,15,0.18)",
              background: "transparent",
              fontSize: 12,
              fontWeight: 700,
              color: "#0B0B0F",
              cursor: "pointer",
              letterSpacing: "0.04em",
            }}
          >
            View Opportunity DNA tab
          </button>
        </div>
      )}
    </div>
  );
}

function OverviewTab({
  summary,
  coverageByCp,
  cpProgress,
  onCpClick,
  onSwitchToHelix,
}: {
  summary: AccountSummary;
  coverageByCp: Record<number, { total: number; answered: number }>;
  cpProgress: CpProgress | null;
  onCpClick: (cp: number) => void;
  onSwitchToHelix?: () => void;
}) {
  const { account, score, greenCount, scoredCount } = summary;

  return (
    <>
      {/* W3-Tower canon: Tower left + Helix right */}
      <TowerHelixPanel
        accountId={String(account.id)}
        accountName={account.name}
        scoredCpCount={scoredCount}
        compositeScore={score}
        cpProgress={cpProgress}
        onSwitchToHelix={onSwitchToHelix}
      />

      {/* Account-level Overview — hero, quick stats, 3-column, M-PACT strip, DMs */}
      <AccountOverviewBlock
        accountId={String(account.id)}
        accountName={account.name}
        industry={account.industry || ""}
        score={score}
        estArr={account.estArr}
        stage={account.stage}
        greenCount={greenCount}
        scoredCount={scoredCount}
        onCpClick={onCpClick}
      />

      {/* Coverage by checkpoint — color-tinted, clickable to jump to Helix */}
      <CoveragePanel coverageByCp={coverageByCp} onCpClick={onCpClick} />

      {/* W15: Next 3 checkpoints to address */}
      <div style={{ marginTop: 24 }}>
        <div
          style={{
            fontFamily: "'DM Sans', Inter, system-ui",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "rgba(11,11,15,0.55)",
            marginBottom: 8,
          }}
        >
          Next 3 checkpoints to address
        </div>
        <NextStepsPanel accountId={account.id} />
      </div>

      {/* 10-cell CP grid — TICKET-3: fastlane skips CP3, CP4, CP7, CP8 */}
      {(() => {
        const customerPath = (account as any).customerPath ?? null;
        const isFastlane = customerPath === "fastlane";
        const visibleDims = isFastlane
          ? CP_DIMS.filter((d) => !FASTLANE_SKIP_CPS.has(d.cp))
          : CP_DIMS;
        return (
          <>
            {isFastlane && (
              <div
                style={{
                  marginTop: 24,
                  marginBottom: 4,
                  fontFamily: "Inter, system-ui",
                  fontSize: 12,
                  color: "rgba(11,11,15,0.55)",
                }}
              >
                Showing 6 active checkpoints. CP3, CP4, CP7, CP8 are not required for this existing-customer account.
              </div>
            )}
            <div data-tour="cp-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-2">
              {visibleDims.map((dim) => {
                // LaneS: use cp-progress status as canonical source; fall back to bant
                const cpProgressEntry = cpProgress?.cps?.find((c) => c.cp_number === dim.cp);
                const bantData = (summary.bant as any)?.[dim.key];
                const cpData: CpData = cpProgressEntry
                  ? { status: cpProgressEntry.status ?? undefined, score: bantData?.score }
                  : (bantData ?? {});
                const cov = coverageByCp[dim.cp] ?? { total: 0, answered: 0 };
                return (
                  <CpCell
                    key={dim.key}
                    accountId={String(account.id)}
                    cpKey={dim.key}
                    cp={dim.cp}
                    label={dim.label}
                    short={dim.short}
                    cpData={cpData}
                    answered={cov.answered}
                    totalQuestions={cov.total}
                    dimensionId={dim.dimensionId}
                    onHelixJump={onCpClick}
                  />
                );
              })}
              {isFastlane && CP_DIMS.filter((d) => FASTLANE_SKIP_CPS.has(d.cp)).map((dim) => (
                <div
                  key={dim.key}
                  style={{
                    borderRadius: 8,
                    border: "1px solid rgba(11,11,15,0.1)",
                    background: "rgba(11,11,15,0.03)",
                    padding: "12px 14px",
                    opacity: 0.55,
                  }}
                >
                  <div style={{ fontFamily: "'DM Sans', Inter, system-ui", fontSize: 11, fontWeight: 700, color: "rgba(11,11,15,0.4)", letterSpacing: "0.08em" }}>
                    CP{dim.cp} — {dim.label}
                  </div>
                  <div style={{ fontFamily: "Inter, system-ui", fontSize: 10, color: "rgba(11,11,15,0.4)", marginTop: 4 }}>
                    Skipped: existing customer
                  </div>
                </div>
              ))}
            </div>
          </>
        );
      })()}

      {/* Explanation */}
      <div className="mt-10 card-shell p-5 text-sm text-muted-foreground">
        <div className="text-foreground text-[13px] uppercase tracking-wider mb-3 font-medium">How this works</div>
        <p className="leading-relaxed">
          AEs answer qualification questions per checkpoint; the platform derives the score and bucket.
          Omitted means we asked and the answer is disqualifying. TBD means not yet assessed.
          Coverage tints reflect how many questions have been answered, not deal health — a red coverage
          tile only means the AE has more intelligence to gather, not that the deal is in jeopardy.
        </p>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB: Decision Tree — New Customer vs Existing Customer side-by-side
// ─────────────────────────────────────────────────────────────────────────────

// Canonical CP titles — locked 2026-05-26 per helix-checkpoints.ts
const DT_NEW_LOGO = [
  { cp: 1,  title: "Project",                               note: "Real project, named sponsor, budget cycle, why-now", hardKill: true  },
  { cp: 2,  title: "Need / Pain / Demonstration",           note: "Pain named, quantified, in the buyer's own words; cost of doing nothing", hardKill: true  },
  { cp: 3,  title: "Budget",                               note: "Real money, named source, validated line item, owner named", hardKill: true  },
  { cp: 4,  title: "Timeline",                             note: "Decision window AND implementation window — both real, both confirmed", hardKill: true  },
  { cp: 5,  title: "Authority — Project / Initiative",      note: "Direct access to the project or initiative authority who can champion this deal", hardKill: false },
  { cp: 6,  title: "Technical and Financial Sign-off",      note: "Technical evaluation and financial approval gates identified and engaged", hardKill: true  },
  { cp: 7,  title: "Org Structure and Influence Map",       note: "Champion, supporter, influencer, detractor — agendas mapped", hardKill: false },
  { cp: 8,  title: "Competitive Position",                  note: "Where you stand vs alternatives in buyer's head, evaluated not assumed", hardKill: false },
  { cp: 9,  title: "Compliance — IT Internal and Industry-Specific", note: "Every IT internal and industry-specific compliance requirement mapped", hardKill: true  },
  { cp: 10, title: "Legal — Contracts, SLAs, Terms",       note: "Risk sequencing, MSA, redlines, paper path, implementation readiness", hardKill: true  },
];

type FastMode = "required" | "refresh" | "new" | "skipped";

const DT_FAST_LANE: { cp: number; title: string; mode: FastMode; note: string }[] = [
  { cp: 1,  title: "Project",                               mode: "refresh",  note: "Confirm a real project for THIS expansion" },
  { cp: 2,  title: "Need / Pain / Demonstration",           mode: "refresh",  note: "Re-confirm need and pain for the expansion scope" },
  { cp: 3,  title: "Budget",                               mode: "required", note: "Required only when expansion has its own budget line item" },
  { cp: 4,  title: "Timeline",                             mode: "required", note: "Expansion has its own timeline" },
  { cp: 5,  title: "Authority — Project / Initiative",      mode: "skipped",  note: "MAP carries forward from original deal" },
  { cp: 6,  title: "Technical and Financial Sign-off",      mode: "new",      note: "New buying unit, new economic buyer, new champion" },
  { cp: 7,  title: "Org Structure and Influence Map",       mode: "skipped",  note: "Existing map carries forward unless room changed" },
  { cp: 8,  title: "Competitive Position",                  mode: "skipped",  note: "We are the incumbent" },
  { cp: 9,  title: "Compliance — IT Internal and Industry-Specific", mode: "required", note: "Sourcing path and compliance validated for the expansion line item" },
  { cp: 10, title: "Legal — Contracts, SLAs, Terms",       mode: "required", note: "Always required" },
];

const FAST_MODE_COLORS: Record<FastMode, string> = {
  required: "#0B0B0F",
  refresh:  "#CA8A04",
  new:      "#DC2626",
  skipped:  "#8E8E94",
};

function DecisionTreeTab() {
  return (
    <div className="space-y-8">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1.5">M-PACT methodology</div>
        <h2 className="text-2xl font-semibold tracking-tight">Decision Tree</h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Two paths through the qualification process. New logo follows all 10 checkpoints in full.
          Existing customer expansion follows the fast lane — carry forward, refresh, or re-qualify per CP.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Path A: New Customer */}
        <div className="card-shell overflow-hidden">
          <div className="px-5 py-4 border-b border-border" style={{ background: "#0B0B0F", color: "#FFFFFF" }}>
            <div className="text-[10px] uppercase tracking-[0.22em] opacity-60 mb-1">Path A</div>
            <div className="text-[17px] font-semibold">New Customer</div>
            <div className="text-[12px] opacity-60 mt-0.5">All 10 checkpoints required</div>
          </div>
          <div className="divide-y divide-border">
            {DT_NEW_LOGO.map((row) => (
              <div key={row.cp} className="flex items-start gap-3 px-5 py-3">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 mt-0.5"
                  style={{ background: "#0B0B0F10", color: "#0B0B0F", border: "1px solid #0B0B0F20" }}
                >
                  {row.cp}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] font-medium text-foreground">CP{row.cp} — {row.title}</span>

                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{row.note}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Path B: Existing Customer Expansion */}
        <div className="card-shell overflow-hidden">
          <div className="px-5 py-4 border-b border-border" style={{ background: "#0B0B0F", color: "#FFFFFF" }}>
            <div className="text-[10px] uppercase tracking-[0.22em] opacity-60 mb-1">Path B</div>
            <div className="text-[17px] font-semibold">Existing Customer — Expansion</div>
            <div className="text-[12px] opacity-60 mt-0.5">Fast lane: carry forward, refresh, or re-qualify</div>
          </div>
          {/* Legend */}
          <div className="px-5 py-3 border-b border-border flex flex-wrap gap-3">
            {(Object.entries(FAST_MODE_COLORS) as [FastMode, string][]).map(([mode, color]) => (
              <span key={mode} className="inline-flex items-center gap-1.5 text-[11px]">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
                <span className="capitalize text-muted-foreground">{mode}</span>
              </span>
            ))}
          </div>
          <div className="divide-y divide-border">
            {DT_FAST_LANE.map((row) => (
              <div key={row.cp} className="flex items-start gap-3 px-5 py-3">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 mt-0.5"
                  style={{
                    background: `${FAST_MODE_COLORS[row.mode]}18`,
                    color: FAST_MODE_COLORS[row.mode],
                    border: `1px solid ${FAST_MODE_COLORS[row.mode]}44`,
                  }}
                >
                  {row.cp}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] font-medium text-foreground">CP{row.cp} — {row.title}</span>
                    <span
                      className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider"
                      style={{
                        background: `${FAST_MODE_COLORS[row.mode]}18`,
                        color: FAST_MODE_COLORS[row.mode],
                        border: `1px solid ${FAST_MODE_COLORS[row.mode]}33`,
                      }}
                    >
                      {row.mode}
                    </span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{row.note}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card-shell p-5 text-sm text-muted-foreground">
        <div className="text-foreground text-[13px] uppercase tracking-wider mb-2 font-medium">M-PACT methodology</div>
        <p className="leading-relaxed">
          The decision tree is Mi6 Group intellectual property. New logo requires all 10 checkpoints
          in sequence — disqualifier checkpoints must pass before a deal can advance. Existing customer expansion follows the fast lane,
          carrying forward validated intel while re-qualifying the incremental scope.
          Disqualifier checkpoints apply in both paths where marked.
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB: Templates
// ─────────────────────────────────────────────────────────────────────────────

function TemplatesTab({ industry }: { industry: string }) {
  const [copied, setCopied] = useState<string | null>(null);
  const [browserOpen, setBrowserOpen] = useState(false);

  async function handlePick(script: any) {
    const text = [script.subject ? `Subject: ${script.subject}` : "", script.body]
      .filter(Boolean)
      .join("\n\n");
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch {}
      document.body.removeChild(ta);
    }
    setCopied(script.title);
    setTimeout(() => setCopied(null), 2000);
  }

  // Static preview — keep the in-account tab inline. Modal opens on demand.
  const previewChannels = [
    { label: "Email Opening", count: 30 },
    { label: "Email Follow-up", count: 30 },
    { label: "LinkedIn DM", count: 30 },
    { label: "Phone Script", count: 30 },
    { label: "Voicemail", count: 30 },
  ];

  return (
    <div className="space-y-4">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1">Templates</div>
        <h2 className="text-2xl font-semibold tracking-tight">Cadence Scripts</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Email, phone, LinkedIn, and voicemail scripts{industry ? ` — filterable for ${industry} accounts` : ""}.
          Open the library to browse and copy a template.
        </p>
      </div>
      {copied && (
        <div
          className="px-4 py-2 rounded text-sm font-medium"
          style={{ background: "#F0FDF4", color: "#16A34A", border: "1px solid #BBF7D0" }}
        >
          Copied: {copied}
        </div>
      )}
      <div className="card-shell p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="text-sm font-semibold">Template Library</div>
            <div className="text-xs text-muted-foreground mt-0.5">150 cadence scripts across 5 channels and 3 personas.</div>
          </div>
          <button
            type="button"
            onClick={() => setBrowserOpen(true)}
            className="text-xs px-3 py-2 min-h-[44px] rounded font-medium"
            style={{ background: "#0B0B0F", color: "#FFFFFF" }}
          >
            Open template library
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-4">
          {previewChannels.map((c) => (
            <div key={c.label} className="border border-neutral-200 rounded px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.label}</div>
              <div className="text-base font-semibold mt-0.5">{c.count}</div>
            </div>
          ))}
        </div>
      </div>
      <TemplateLibraryBrowser
        open={browserOpen}
        onClose={() => setBrowserOpen(false)}
        onPick={handlePick}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB: Activity Log
// ─────────────────────────────────────────────────────────────────────────────

interface ActivityEntry {
  id: string;
  date: string;
  type: "Call" | "Email" | "Meeting" | "Demo" | "Proposal" | "Note";
  summary: string;
  rep: string;
  outcome?: string;
}

// Tenant 2 (Mi6 Demo) sample data only — real tenants see empty state until logged.
const MOCK_ACTIVITIES: ActivityEntry[] = [
  {
    id: "a1",
    date: "2026-05-06",
    type: "Meeting",
    summary: "Discovery call — confirmed budget cycle opens Q3. Economic buyer TBD.",
    rep: "AE",
    outcome: "Schedule demo with ops team",
  },
  {
    id: "a2",
    date: "2026-05-03",
    type: "Email",
    summary: "Sent ROI model and case study from comparable manufacturing customer.",
    rep: "AE",
    outcome: "Awaiting response",
  },
  {
    id: "a3",
    date: "2026-04-28",
    type: "Call",
    summary: "Cold outreach — connected with VP Operations. Confirmed pain point around scheduling.",
    rep: "BDR",
    outcome: "Warm handoff to AE",
  },
  {
    id: "a4",
    date: "2026-04-22",
    type: "Demo",
    summary: "Operator demo — showed system in their context. Three must-haves captured.",
    rep: "SE",
    outcome: "Follow-up on integration questions",
  },
  {
    id: "a5",
    date: "2026-04-15",
    type: "Proposal",
    summary: "Submitted commercial proposal. 12-month ARR, standard terms.",
    rep: "AE",
    outcome: "Legal review in progress",
  },
];

const ACTIVITY_COLORS: Record<string, string> = {
  Call:     "#0B0B0F",
  Email:    "#DC2626",
  Meeting:  "#0B0B0F",
  Demo:     "#16A34A",
  Proposal: "#CA8A04",
  Note:     "#6B6B70",
};

function ActivityLogTab({ accountId, accountName }: { accountId: string; accountName: string }) {
  const tenantCtx = getCurrentTenant();
  const isDemo = tenantCtx?.is_demo === true;
  const activities = isDemo ? MOCK_ACTIVITIES : [];
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1">Activity Log</div>
          <h2 className="text-2xl font-semibold tracking-tight">{accountName}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            All recorded activities for this account, most recent first.
          </p>
        </div>
        <Link
          href="/log/ae"
          className="text-xs text-[#0B0B0F] inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded border border-[#0B0B0F] hover:bg-[#0B0B0F] hover:text-white self-start sm:self-auto whitespace-nowrap transition-colors"
        >
          Open full log
        </Link>
      </div>

      <div className="space-y-3">
        {activities.length === 0 && (
          <div className="card-shell p-8 text-center text-sm text-muted-foreground">
            No activity logged yet for this account. Use Open full log to add the first entry.
          </div>
        )}
        {activities.map((entry) => (
          <div key={entry.id} className="card-shell p-4 flex gap-4">
            <div className="flex flex-col items-center gap-1 shrink-0">
              <div
                className="px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider"
                style={{
                  background: `${ACTIVITY_COLORS[entry.type]}18`,
                  color: ACTIVITY_COLORS[entry.type],
                  border: `1px solid ${ACTIVITY_COLORS[entry.type]}33`,
                }}
              >
                {entry.type}
              </div>
              <div className="text-[10px] text-muted-foreground tabular whitespace-nowrap">{entry.date}</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-foreground leading-relaxed">{entry.summary}</div>
              {entry.outcome && (
                <div className="mt-1.5 text-[11px] text-muted-foreground">
                  <span className="uppercase tracking-wider text-[9px]">Next action: </span>
                  {entry.outcome}
                </div>
              )}
              <div className="mt-1 text-[10px] text-muted-foreground uppercase tracking-wider">{entry.rep}</div>
            </div>
          </div>
        ))}
      </div>

      {activities.length > 0 && (
        <div className="text-[11px] text-muted-foreground">
          Showing last 5 activities. Open the full activity log for the complete history.
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB: Coaching
// ─────────────────────────────────────────────────────────────────────────────

interface CoachingItem {
  id: string;
  cp: number;
  cpLabel: string;
  trigger: string;
  recommendation: string;
  urgency: "high" | "medium" | "low";
}

function buildCoachingItems(): CoachingItem[] {
  return [
    {
      id: "c1",
      cp: 6,
      cpLabel: "Technical and Financial Sign-off",
      trigger: "Economic buyer not yet identified or met",
      recommendation:
        "Schedule a call with the champion to get an introduction to the economic buyer before end of month. Without EB engagement, deals stall at contract stage.",
      urgency: "high",
    },
    {
      id: "c2",
      cp: 4,
      cpLabel: "Budget",
      trigger: "Budget range identified but line item not validated",
      recommendation:
        "Ask the champion to confirm whether budget is a named line item in the current fiscal plan or still discretionary. Discretionary budget rarely closes on schedule.",
      urgency: "high",
    },
    {
      id: "c3",
      cp: 7,
      cpLabel: "Org Structure and Influence Map",
      trigger: "Detractors not yet mapped",
      recommendation:
        "Ask: who in the room has used a competing system before and would push back on change? Map that person. Understand their agenda before the proposal stage.",
      urgency: "medium",
    },
    {
      id: "c4",
      cp: 10,
      cpLabel: "Legal — Contracts, SLAs, Terms",
      trigger: "MSA not yet circulated",
      recommendation:
        "Start the paper path early. Legal reviews at this account average 3–4 weeks. Send the standard MSA now and ask who on their side owns contract review.",
      urgency: "medium",
    },
    {
      id: "c5",
      cp: 9,
      cpLabel: "Compliance — IT Internal and Industry-Specific",
      trigger: "Payment terms and capex/opex classification pending",
      recommendation:
        "Confirm whether this is a capex or opex purchase. Different approval thresholds and timelines apply. Get the sourcing path in writing before the next QBR.",
      urgency: "low",
    },
  ];
}

const URGENCY_COLORS: Record<string, string> = {
  high:   "#DC2626",
  medium: "#CA8A04",
  low:    "#6B6B70",
};

// D-050: account-level coaching note shape from server
interface AccountCoachingNote {
  id: string;
  body: string;
  created_at: string;
  author_name?: string;
}

function CoachingTab({ accountId, accountName }: { accountId: string; accountName: string }) {
  const items = useMemo(() => buildCoachingItems(), []);
  const session = getSession();
  const isManager = session?.role === "manager" || session?.role === "executive";

  // D-050: manager coaching notes state
  const [noteBody, setNoteBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedNotes, setSavedNotes] = useState<AccountCoachingNote[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState<string | null>(null);

  // Load saved notes on mount
  useEffect(() => {
    if (!accountId) return;
    setNotesLoading(true);
    api<{ notes: AccountCoachingNote[] }>(`/api/accounts/${accountId}/coaching-notes`)
      .then((r) => setSavedNotes(r?.notes ?? []))
      .catch((e: any) => setNotesError(e?.message ?? "Failed to load notes"))
      .finally(() => setNotesLoading(false));
  }, [accountId]);

  async function saveNote() {
    if (!noteBody.trim() || saving) return;
    setSaving(true);
    try {
      await api(`/api/accounts/${accountId}/coaching-notes`, {
        method: "POST",
        body: JSON.stringify({ body: noteBody.trim() }),
      });
      setNoteBody("");
      const r = await api<{ notes: AccountCoachingNote[] }>(`/api/accounts/${accountId}/coaching-notes`);
      setSavedNotes(r?.notes ?? []);
    } catch (e: any) {
      setNotesError(e?.message ?? "Failed to save note");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1">Coaching</div>
        <h2 className="text-2xl font-semibold tracking-tight">{accountName}</h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Auto-generated coaching signals from qualification gaps. Address high urgency items before the next customer touchpoint.
        </p>
      </div>

      {/* D-050: Manager coaching note input */}
      {isManager && (
        <div className="card-shell p-5 space-y-3">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">Add a coaching note</div>
          <textarea
            value={noteBody}
            onChange={(e) => setNoteBody(e.target.value)}
            placeholder="Enter a coaching note for this account..."
            rows={3}
            className="w-full text-sm px-3 py-2 rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-foreground/30 resize-y"
            data-testid="coaching-note-input"
          />
          {notesError && (
            <div className="text-[12px] text-rose-600">{notesError}</div>
          )}
          <button
            onClick={() => void saveNote()}
            disabled={!noteBody.trim() || saving}
            className="text-[12px] px-4 py-2 rounded bg-foreground text-background hover:opacity-90 disabled:opacity-50 font-medium transition-opacity"
            data-testid="save-coaching-note-btn"
          >
            {saving ? "Saving..." : "Save coaching note"}
          </button>
        </div>
      )}

      <div className="space-y-4">
        {items.map((item) => (
          <div key={item.id} className="card-shell overflow-hidden">
            <div className="flex items-start gap-4 p-5">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0"
                style={{ background: "#0B0B0F10", color: "#0B0B0F", border: "2px solid #0B0B0F20" }}
              >
                {item.cp}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[13px] font-semibold text-foreground">
                    CP{item.cp} — {item.cpLabel}
                  </span>
                  <span
                    className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider"
                    style={{
                      background: `${URGENCY_COLORS[item.urgency]}18`,
                      color: URGENCY_COLORS[item.urgency],
                      border: `1px solid ${URGENCY_COLORS[item.urgency]}33`,
                    }}
                  >
                    {item.urgency}
                  </span>
                </div>
                <div className="text-[12px] text-muted-foreground mb-2">
                  <span className="uppercase tracking-wider text-[10px]">Trigger: </span>
                  {item.trigger}
                </div>
                <p className="text-[13px] text-foreground leading-relaxed">{item.recommendation}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* D-050: Saved manager notes in reverse-chron order */}
      {(savedNotes.length > 0 || notesLoading) && (
        <div className="space-y-3">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">Coaching notes</div>
          {notesLoading && (
            <div className="text-sm text-muted-foreground">Loading notes...</div>
          )}
          {savedNotes.map((note) => (
            <div key={note.id} className="card-shell p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{note.author_name ?? "Manager"}</span>
                <span className="text-[10px] text-muted-foreground">
                  {new Date(note.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </span>
              </div>
              <p className="text-[13px] text-foreground leading-relaxed">{note.body}</p>
            </div>
          ))}
        </div>
      )}

      <Link
        href="/coaching"
        className="text-xs text-[#0B0B0F] inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded border border-[#0B0B0F] hover:bg-[#0B0B0F] hover:text-white transition-colors"
      >
        Open full coaching feed
      </Link>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Coverage panel + CP cells
// ─────────────────────────────────────────────────────────────────────────────

function CoveragePanel({
  coverageByCp,
  onCpClick,
}: {
  coverageByCp: Record<number, { total: number; answered: number }>;
  onCpClick: (cp: number) => void;
}) {
  const totalAnswered = Object.values(coverageByCp).reduce((s, c) => s + c.answered, 0);
  const totalQuestions = Object.values(coverageByCp).reduce((s, c) => s + c.total, 0);
  const overallPct = totalQuestions > 0 ? Math.round((totalAnswered / totalQuestions) * 100) : 0;

  return (
    <div className="card-shell p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            Coverage by checkpoint
          </div>
          <div className="text-[13px] text-foreground mt-1">
            <span className="font-semibold">{totalAnswered}</span>
            <span className="text-muted-foreground">/{totalQuestions} questions answered </span>
            <span className="text-muted-foreground">· {overallPct}% overall</span>
          </div>
        </div>
        <div className="text-[11px] text-muted-foreground max-w-xs text-right hidden sm:block">
          Coverage shows intel gathered, not deal health. Reds and oranges are
          places to ask more questions, not warnings the deal is in trouble.
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-10 gap-2">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((cp) => {
          const cov = coverageByCp[cp] ?? { total: 0, answered: 0 };
          const tint: CoverageTint = coverageTint(cov.answered, cov.total);
          const s = COVERAGE_STYLES[tint];
          const pct = cov.total > 0 ? Math.round((cov.answered / cov.total) * 100) : 0;
          return (
            <button
              key={cp}
              className={`rounded border ${s.bg} ${s.border} p-2.5 text-left cursor-pointer hover:ring-1 hover:ring-[#0B0B0F] transition-all`}
              title={`CP${cp} · ${cov.answered}/${cov.total} (${pct}%) · Click to jump to Helix tab`}
              onClick={() => onCpClick(cp)}
            >
              <div className={`text-[10px] uppercase tracking-wider ${s.text}`}>CP{cp}</div>
              <div className={`text-[16px] font-semibold tabular ${s.text} mt-0.5`}>
                {cov.answered}
                <span className="text-[12px] font-normal opacity-70">/{cov.total}</span>
              </div>
              <div className="mt-1.5 h-1 rounded bg-white/60 overflow-hidden">
                <div className={`h-full ${s.bar}`} style={{ width: `${pct}%` }} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface CpData {
  status?: "green" | "amber" | "red" | "tbd" | string;
  score?: number;
  answer?: string;
}

function cpStatusToBand(status?: string): ColorBand {
  switch (status) {
    case "green": return "green";
    case "amber": return "amber";
    case "red":   return "red";
    default:      return "tbd";
  }
}

function CpCell({
  accountId,
  cpKey,
  cp,
  label,
  short,
  cpData,
  answered,
  totalQuestions,
  dimensionId,
  onHelixJump,
}: {
  accountId: string;
  cpKey: string;
  cp: number;
  label: string;
  short: string;
  cpData: CpData | undefined;
  answered: number;
  totalQuestions: number;
  dimensionId: string;
  onHelixJump: (cp: number) => void;
}) {
  const band = cpStatusToBand(cpData?.status);
  const s = bandStyle(band);
  const score = cpData?.score ?? 0;
  const tint: CoverageTint = coverageTint(answered, totalQuestions);
  const cs = COVERAGE_STYLES[tint];
  const pct = totalQuestions > 0 ? Math.round((answered / totalQuestions) * 100) : 0;
  const coaching = CP_COACHING[cp];

  // D-031 fix: clicking a CP card now jumps to the Helix tab focused on that
  // CP, which is the authoritative per-CP detail view. Previously cards routed
  // to /dimension/<scrambled_id> which opened the wrong dimension (e.g. CP1
  // Project opened IT/Security). The dimension page itself is also linked
  // separately via the "Open question set" affordance below for users who
  // need the underlying question set.
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onHelixJump(cp)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onHelixJump(cp);
        }
      }}
      className={`card-shell-2 p-4 group transition-colors hover:ring-1 ${s.ring} flex flex-col cursor-pointer`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {short}·CP{cp}
          </div>
          <div className="text-[15px] font-medium tracking-tight mt-0.5 text-foreground">
            {label}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100" />
      </div>

      <div className="mt-3 flex items-baseline gap-2">
        <span className={`text-3xl font-semibold tabular tracking-tight ${s.text}`}>
          {band === "tbd" ? "—" : score.toFixed(0)}
        </span>
        <BandPill band={band} />
      </div>

      <div className="mt-3" title="Composite score band">
        <BandBar band={band} value={band === "tbd" ? 0 : score} />
      </div>

      {/* Coverage chip — C-004: clickable to jump to Helix tab at that CP */}
      <button
        className={`mt-3 inline-flex items-center self-start gap-2 px-2 py-1 rounded border ${cs.bg} ${cs.border} ${cs.text} hover:ring-1 hover:ring-[#0B0B0F] transition-all`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onHelixJump(cp);
        }}
        title={`Jump to CP${cp} in Helix tab`}
      >
        <span className="text-[10px] uppercase tracking-wider opacity-70">Coverage</span>
        <span className="text-[11px] font-medium tabular">
          {answered}/{totalQuestions} · {pct}%
        </span>
      </button>

      {coaching && (
        <div className="mt-3 pt-3 border-t border-border space-y-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-neutral-200 bg-neutral-50 text-neutral-900 text-[10px]">
              <span className="opacity-60 uppercase tracking-wider text-[9px]">Stage</span>
              <span className="font-medium">{coaching.stage}</span>
            </span>

          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
              Coaching · why this checkpoint
            </div>
            <p className="text-[12px] text-foreground/85 leading-relaxed line-clamp-3">
              {coaching.purpose}
            </p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
              On the fence
            </div>
            <p className="text-[12px] text-muted-foreground leading-relaxed line-clamp-2">
              {coaching.onTheFence}
            </p>
          </div>
        </div>
      )}

      {/* D-031 fix: keep the underlying dimension question set discoverable
          as a secondary affordance, separate from the primary card click. */}
      <div className="mt-2">
        <Link
          href={`/account/${accountId}/dimension/${dimensionId}`}
          onClick={(e) => e.stopPropagation()}
          className="text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
        >
          Open question set →
        </Link>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB: BANT
// ─────────────────────────────────────────────────────────────────────────────

interface BantCell {
  confirmed?: boolean;
  evidence?: string;
}

function BantTab({
  account,
  bant,
}: {
  account: ApiAccount;
  bant: Record<string, unknown>;
}) {
  const ext = account as ApiAccount & {
    bantNeed?: string | null;
    bantBudget?: string | null;
    bantAuthority?: string | null;
    bantTimeline?: string | null;
  };
  const cell = (key: "Need" | "Budget" | "Authority" | "Timeline", fallback: string | null | undefined): BantCell => {
    const raw = (bant as Record<string, BantCell | undefined>)[key];
    if (raw && (raw.confirmed || (raw.evidence && raw.evidence.length > 0))) return raw;
    if (fallback && fallback.trim().length > 0) {
      return { confirmed: true, evidence: fallback };
    }
    return { confirmed: false, evidence: "" };
  };
  const rows: { label: string; cell: BantCell }[] = [
    { label: "Need",      cell: cell("Need",      ext.bantNeed) },
    { label: "Budget",    cell: cell("Budget",    ext.bantBudget) },
    { label: "Authority", cell: cell("Authority", ext.bantAuthority) },
    { label: "Timeline",  cell: cell("Timeline",  ext.bantTimeline) },
  ];
  const anyConfirmed = rows.some((r) => r.cell.confirmed || (r.cell.evidence && r.cell.evidence.length > 0));

  return (
    <div className="card-shell p-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold tracking-tight">Qualifier Letters</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Need · Budget · Authority · Timeline
        </p>
      </div>
      {!anyConfirmed ? (
        <div className="rounded-md border border-dashed border-border p-6 text-sm text-muted-foreground">
          Not yet captured. Qualifier letters will appear here once Need, Budget,
          Authority, or Timeline are recorded for this account.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rows.map((r) => {
            const filled = r.cell.confirmed || (r.cell.evidence && r.cell.evidence.length > 0);
            return (
              <div
                key={r.label}
                className="rounded-md border border-border p-4"
                style={{ background: filled ? "#FFFFFF" : "#FAFAF7" }}
              >
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1.5">
                  {r.label}
                </div>
                {filled ? (
                  <p className="text-[13px] text-foreground leading-relaxed whitespace-pre-wrap">
                    {r.cell.evidence}
                  </p>
                ) : (
                  <p className="text-[12px] text-muted-foreground italic">Not yet captured</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB: Stakeholders
// ─────────────────────────────────────────────────────────────────────────────

function StakeholdersTab({ accountId }: { accountId: number }) {
  if (!Number.isFinite(accountId) || accountId <= 0) {
    return (
      <div className="card-shell p-6">
        <div className="rounded-md border border-dashed border-border p-6 text-sm text-muted-foreground">
          No stakeholders mapped. Save this account first, then add stakeholders.
        </div>
      </div>
    );
  }
  return (
    <div className="card-shell p-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold tracking-tight">Stakeholders</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Decision makers, champions, and influencers for this opportunity.
        </p>
      </div>
      <DecisionMakerPanel accountId={accountId} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB: 30-60-90
// ─────────────────────────────────────────────────────────────────────────────

interface PlanAction {
  text?: string;
  due?: string | null;
  done?: boolean;
}

interface Plan306090 {
  d30?: PlanAction[];
  d60?: PlanAction[];
  d90?: PlanAction[];
}

function Plan306090Tab({ accountId }: { accountId: number }) {
  const [plan, setPlan] = useState<Plan306090 | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!Number.isFinite(accountId) || accountId <= 0) {
      setLoading(false);
      return;
    }
    let mounted = true;
    setLoading(true);
    setError(null);
    api<any>(`/api/accounts/${accountId}`)
      .then((row) => {
        if (!mounted) return;
        const ext = (row && (row.aip_ext || row.aipExt)) || {};
        const p = ext.plan30_60_90 || ext.plan_30_60_90 || null;
        setPlan(p);
      })
      .catch((e) => {
        if (!mounted) return;
        setError(friendlyError(e));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [accountId]);

  const buckets: { id: keyof Plan306090; label: string }[] = [
    { id: "d30", label: "First 30 Days" },
    { id: "d60", label: "Days 31-60"    },
    { id: "d90", label: "Days 61-90"    },
  ];

  const totalActions = plan
    ? (plan.d30?.length ?? 0) + (plan.d60?.length ?? 0) + (plan.d90?.length ?? 0)
    : 0;

  return (
    <div className="card-shell p-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold tracking-tight">30-60-90 Plan</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Actions and milestones across the next 90 days.
        </p>
      </div>
      {loading ? (
        <div className="text-sm text-muted-foreground">Loading...</div>
      ) : error ? (
        <div className="rounded-md border border-dashed border-border p-6 text-sm text-muted-foreground">
          No 30-60-90 plan yet.
        </div>
      ) : !plan || totalActions === 0 ? (
        <div className="rounded-md border border-dashed border-border p-6 text-sm text-muted-foreground">
          No 30-60-90 plan yet. Add actions here once they are captured for this account.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {buckets.map((b) => {
            const actions = plan[b.id] ?? [];
            return (
              <div key={b.id} className="rounded-md border border-border p-4 bg-white">
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-2">
                  {b.label}
                </div>
                {actions.length === 0 ? (
                  <p className="text-[12px] text-muted-foreground italic">No actions</p>
                ) : (
                  <ul className="space-y-2">
                    {actions.map((a, idx) => (
                      <li
                        key={idx}
                        className="text-[13px] leading-snug flex items-start gap-2"
                      >
                        <span
                          className="inline-block w-3 h-3 rounded-sm mt-1 flex-shrink-0"
                          style={{
                            background: a.done ? "#16A34A" : "#E5E5E7",
                            border: "1px solid #D4D4D8",
                          }}
                        />
                        <span className="flex-1">
                          <span
                            className={a.done ? "line-through text-muted-foreground" : "text-foreground"}
                          >
                            {a.text ?? "(no text)"}
                          </span>
                          {a.due && (
                            <span className="block text-[10px] text-muted-foreground mt-0.5">
                              due {a.due}
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// W39: export default wraps inner component with per-route error boundary.
// accountId is passed as a prop so componentDidUpdate can reset the boundary
// when the user navigates to a different account. This prevents stale error
// state from persisting after route change.
export default function AccountScorecard() {
  const { id } = useParams<{ id: string }>();
  return (
    <AccountScorecardErrorBoundary accountId={id}>
      <AccountScorecardInner />
    </AccountScorecardErrorBoundary>
  );
}
